import React from 'react';
import cx from 'classnames';

import PrescriptionCard, { PrescriptionCardStatus } from '../PrescriptionCard';
import TestCard, { BookingData, TestCardStatus, LabData } from '../TestCard';
import TreatmentCard, { TreatmentStep } from '../TreatmentCard';

import styles from './index.module.scss';

interface Treatment {
  iconUrl: string;
  type: string;
  activeStepId: string;
  testCode: string;
  doctorName: string;
  startDate: string;
  id: string;
  steps: TreatmentStep[];
}

interface Prescription {
  activationFlowUrl: string;
  iconUrl: string;
  lastUpdate: string;
  name: string;
  nextReorderDate: string;
  reorderUrl: string;
  status: string;
  type: string;
  customerName: string;
}

interface Test {
  activationFlowUrl: string;
  code: string;
  iconUrl: string;
  lastUpdate: string;
  personName: string;
  reorderDiscount: string;
  reorderUrl: string;
  samplingInstructionsUrl: string;
  status: string;
  testResultsUrl: string;
  type: string;
  bookingData?: BookingData;
  testedPersonName: string;
  updateSamplingTimeUrl: string;
  takeSampleUrl: string;
  testedPersonInitials: string;
  resultsViewed: boolean | null;
  partnerCode: string | null;
  labData: LabData;
}

interface DashboardCardsListProps {
  prescriptions: Prescription[];
  tests: Test[];
  treatments: Treatment[];
  arePrescriptionsVisible: boolean;
  areTestsVisible: boolean;
  areTreatmentsVisible: boolean;
}

const DashboardCardsList: React.FC<DashboardCardsListProps> = ({
  prescriptions,
  tests,
  treatments,
  arePrescriptionsVisible,
  areTestsVisible,
  areTreatmentsVisible,
}) => {
  return (
    <div className={styles.wrapper}>
      {areTreatmentsVisible && (
        <div
          className={cx({
            [styles.cards]: true,
            [styles.treatments]: true,
          })}
        >
          {treatments.map((treatment, index) => (
            <TreatmentCard
              key={`t-${index}`}
              iconUrl={treatment.iconUrl}
              type={treatment.type}
              activeStepId={treatment.activeStepId}
              testCode={treatment.testCode}
              doctorName={treatment.doctorName}
              startDate={treatment.startDate}
              id={treatment.id}
              steps={treatment.steps}
            />
          ))}
        </div>
      )}
      {areTestsVisible && (
        <div className={styles.cards}>
          {tests.map((test, index) => (
            <TestCard
              key={`t-${index}`}
              iconUrl={test.iconUrl}
              type={test.type}
              status={test.status as TestCardStatus}
              activationFlowUrl={test.activationFlowUrl}
              code={test.code}
              partnerCode={test.partnerCode}
              lastUpdate={test.lastUpdate}
              personName={test.personName}
              reorderUrl={test.reorderUrl}
              reorderDiscount={test.reorderDiscount}
              testResultsUrl={test.testResultsUrl}
              bookingData={test.bookingData}
              samplingInstructionsUrl={test.samplingInstructionsUrl}
              updateSamplingTimeUrl={test.updateSamplingTimeUrl}
              takeSampleUrl={test.takeSampleUrl}
              testedPersonName={test.testedPersonName}
              testedPersonInitials={test.testedPersonInitials}
              resultsViewed={test.resultsViewed}
              labData={test.labData}
            />
          ))}
        </div>
      )}

      {arePrescriptionsVisible && (
        <div
          className={cx({
            [styles.cards]: true,
            [styles.prescriptions]: true,
          })}
        >
          {prescriptions.map((prescription, index) => (
            <PrescriptionCard
              key={`p-${index}`}
              iconUrl={prescription.iconUrl}
              name={prescription.name}
              status={prescription.status as PrescriptionCardStatus}
              activationFlowUrl={prescription.activationFlowUrl}
              reorderUrl={prescription.reorderUrl}
              lastUpdate={prescription.lastUpdate}
              nextReorderDate={prescription.nextReorderDate}
              customerName={prescription.customerName}
            />
          ))}
        </div>
      )}
    </div>
  );
};
export default DashboardCardsList;
